import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { BWHeader, getStyle } from '../dist/helpers';

const WS_URL = 'wss://api.andrewdilks.com/chat';

const formatTime = (ts) => {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// ── Login gate ────────────────────────────────────────────────────
function LoginGate({ dark }) {
  return (
    <div>
      <center>
        {BWHeader(dark, '💬 Chatroom', 'Chat with other visitors in real time')}
        <br />
        <div className="container" style={{ maxWidth: '420px' }}>
          <div className="p-4 text-center" style={getStyle(dark)}>
            <p className={`mb-3 ${dark ? 'text-white' : ''}`}>
              You need to be logged in to join the chat.
            </p>
            <Link to="/login" className="btn btn-primary px-4">Log in</Link>
          </div>
        </div>
      </center>
    </div>
  );
}

// ── Single message bubble ─────────────────────────────────────────
function MessageBubble({ msg, isMe, dark }) {
  if (msg.kind === 'system') {
    return (
      <div style={{
        textAlign: 'center',
        fontSize: '0.75rem',
        opacity: 0.4,
        margin: '0.5rem 0',
        fontStyle: 'italic',
        color: dark ? 'white' : 'inherit',
      }}>
        — {msg.text} —
      </div>
    );
  }

  return (
    // Row: push bubble to the correct side
    <div style={{
      display: 'flex',
      justifyContent: isMe ? 'flex-end' : 'flex-start',
      marginBottom: '0.75rem',
    }}>
      {/* Column: username → bubble → timestamp, all capped at 70% width */}
      <div style={{
        maxWidth: '70%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: isMe ? 'flex-end' : 'flex-start',
      }}>
        {/* Username (others only) */}
        {!isMe && (
          <div style={{
            fontSize: '0.7rem',
            fontWeight: 700,
            opacity: 0.55,
            marginBottom: '0.18rem',
            paddingLeft: '0.4rem',
            color: dark ? 'white' : 'inherit',
            textAlign: 'left',
            width: '100%',
          }}>
            {msg.username}
          </div>
        )}

        {/* Bubble */}
        <div style={{
          padding: '0.5rem 0.9rem',
          borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
          background: isMe
            ? (dark ? 'rgba(80, 140, 220, 0.6)' : 'rgba(13, 110, 253, 0.15)')
            : (dark ? 'rgba(255,255,255,0.13)' : 'rgba(0,0,0,0.09)'),
          color: dark ? 'white' : '#111',
          wordBreak: 'break-word',
          fontSize: '0.93rem',
          lineHeight: 1.5,
        }}>
          {msg.text}
        </div>

        {/* Timestamp below bubble */}
        <span style={{
          fontSize: '0.65rem',
          opacity: 0.38,
          marginTop: '0.18rem',
          paddingLeft: isMe ? 0 : '0.4rem',
          paddingRight: isMe ? '0.4rem' : 0,
          color: dark ? 'white' : 'inherit',
        }}>
          {formatTime(msg.timestamp)}
        </span>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────
export default function Chatroom({ dark, userData }) {
  const [messages, setMessages] = useState([]);
  const [online, setOnline]     = useState([]);
  const [input, setInput]       = useState('');
  const [status, setStatus]     = useState('connecting'); // connecting | open | closed
  const wsRef                   = useRef(null);
  const bottomRef               = useRef(null);
  const inputRef                = useRef(null);
  const isLoggedIn              = !!userData?.username;

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // WebSocket lifecycle
  useEffect(() => {
    if (!isLoggedIn) return;

    let dead = false;
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;
    setStatus('connecting');

    ws.onopen = () => {
      if (dead) return ws.close();
      ws.send(JSON.stringify({ type: 'join', username: userData.username }));
      setStatus('open');
    };

    ws.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      if (msg.type === 'init') {
        setMessages(msg.history.map(m => ({ ...m, kind: 'message' })));
        setOnline(msg.online ?? []);
      } else if (msg.type === 'message') {
        setMessages(prev => [...prev, { ...msg, kind: 'message' }]);
      } else if (msg.type === 'join') {
        setOnline(prev => [...new Set([...prev, msg.username])]);
        setMessages(prev => [...prev, { kind: 'system', text: `${msg.username} joined` }]);
      } else if (msg.type === 'leave') {
        setOnline(prev => prev.filter(u => u !== msg.username));
        setMessages(prev => [...prev, { kind: 'system', text: `${msg.username} left` }]);
      }
    };

    ws.onclose = () => { if (!dead) setStatus('closed'); };
    ws.onerror = () => { if (!dead) setStatus('closed'); };

    return () => {
      dead = true;
      ws.close();
    };
  }, [isLoggedIn, userData?.username]);

  const sendMessage = useCallback(() => {
    const text = input.trim();
    if (!text || wsRef.current?.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: 'message', text }));
    setInput('');
    inputRef.current?.focus();
  }, [input]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isLoggedIn) return <LoginGate dark={dark} />;

  const border = dark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)';
  const statusColor = { connecting: '#f0c040', open: '#4caf50', closed: '#e57373' }[status];
  const statusLabel = { connecting: 'Connecting…', open: 'Connected', closed: 'Disconnected' }[status];

  return (
    <div>
      <center>
        {BWHeader(dark, '💬 Chatroom', 'Chat with other visitors in real time')}
        <br />

        <div style={{
          ...getStyle(dark),
          display: 'flex',
          flexDirection: 'column',
          width: '92%',
          maxWidth: '860px',
          height: '72vh',
          overflow: 'hidden',
        }}>

          {/* Top status bar */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.5rem 1rem',
            borderBottom: border,
            flexShrink: 0,
          }}>
            <span style={{ fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '0.4rem', color: dark ? 'white' : 'inherit' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor, display: 'inline-block' }} />
              {statusLabel}
            </span>
            <span style={{ fontSize: '0.78rem', opacity: 0.5, color: dark ? 'white' : 'inherit' }}>
              {online.length} online
            </span>
          </div>

          {/* Body: messages + online sidebar */}
          <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

            {/* Message list */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.2rem' }}>
              {messages.length === 0 && status === 'open' && (
                <div style={{ textAlign: 'center', opacity: 0.4, marginTop: '2.5rem', fontSize: '0.9rem', color: dark ? 'white' : 'inherit' }}>
                  No messages yet — say hello! 👋
                </div>
              )}
              {messages.map((msg, i) => (
                <MessageBubble
                  key={i}
                  msg={msg}
                  isMe={msg.username === userData.username}
                  dark={dark}
                />
              ))}
              <div ref={bottomRef} />
            </div>

            {/* Online users */}
            <div style={{
              width: '148px',
              flexShrink: 0,
              borderLeft: border,
              padding: '0.85rem 0.7rem',
              overflowY: 'auto',
            }}>
              <div style={{
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: '0.07em',
                opacity: 0.45,
                marginBottom: '0.55rem',
                color: dark ? 'white' : 'inherit',
              }}>
                ONLINE — {online.length}
              </div>
              {online.map(u => (
                <div key={u} style={{
                  fontSize: '0.82rem',
                  padding: '0.2rem 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  color: dark ? 'white' : 'inherit',
                  fontWeight: u === userData.username ? 700 : 400,
                }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#4caf50', flexShrink: 0 }} />
                  {u === userData.username ? `${u} (you)` : u}
                </div>
              ))}
            </div>

          </div>

          {/* Input row */}
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            padding: '0.65rem 1rem',
            borderTop: border,
            flexShrink: 0,
          }}>
            <input
              ref={inputRef}
              className="form-control"
              placeholder={status === 'open' ? 'Type a message… (Enter to send)' : statusLabel}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={status !== 'open'}
              style={{ flex: 1, fontSize: '0.92rem' }}
            />
            <button
              className="btn btn-primary"
              onClick={sendMessage}
              disabled={status !== 'open' || !input.trim()}
              style={{ fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              Send
            </button>
          </div>

        </div>
        <br /><br />
      </center>
    </div>
  );
}
